import { apiJson, createIdempotencyKey } from '../api';
import { createSignaturePad } from '../components/signature';
import { mountTurnstile, type TurnstileControl } from '../turnstile';

type OfferMeta = {
  status: string;
  employeeName: string;
  offerReference: string;
  position: string;
  acceptanceDeadline?: string;
};

export async function renderOfferPage(root: HTMLElement): Promise<void> {
  const token = new URLSearchParams(location.search).get('t') || '';
  root.innerHTML = `<main class="offer-page"><section class="letter-page"><h1>Loading offer...</h1></section></main>`;
  if (!token) { root.innerHTML = `<main class="offer-page"><section class="letter-page"><h1>Offer unavailable</h1></section></main>`; return; }

  try {
    const meta = await apiJson<OfferMeta>(`/api/offer/${encodeURIComponent(token)}`);
    const terminal = ['SIGNED','DECLINED','EXPIRED','CANCELLED'].includes(meta.status);
    root.innerHTML = `
      <main class="offer-page">
        <section class="offer-header">
          <div>
            <div class="brand">Stupiak Recruitment</div>
            <h1>Offer Letter</h1>
            <p>${escapeHtml(meta.employeeName)} · ${escapeHtml(meta.position || 'Position to be confirmed')} · ${escapeHtml(meta.offerReference)}</p>
            ${meta.acceptanceDeadline ? `<p class="offer-expiry">Signing access valid until ${escapeHtml(meta.acceptanceDeadline)}</p>` : ''}
          </div>
          <div class="offer-status">${escapeHtml(meta.status)}</div>
        </section>
        ${terminal ? `
          <section class="letter-page terminal-letter">
            <div class="section-title">Offer Status</div>
            <h2>Offer ${escapeHtml(meta.status.toLowerCase())}</h2>
            <p>${meta.status === 'EXPIRED' ? 'The signing window has ended. Please contact the company if a new offer link is required.' : 'This offer can no longer be changed from this signing page.'}</p>
          </section>
          ${meta.status === 'SIGNED' ? `<section class="offer-document"><iframe class="pdf-frame" src="/api/offer/${encodeURIComponent(token)}/pdf" title="Signed Offer Letter"></iframe></section>` : ''}
        ` : `
          <section class="offer-document">
            <div class="letter-toolbar">
              <div>
                <b>Review the complete offer letter</b>
                <span>Read every page before signing below.</span>
              </div>
              <a class="button secondary" href="/api/offer/${encodeURIComponent(token)}/pdf" target="_blank" rel="noreferrer">Open PDF</a>
            </div>
            <iframe class="pdf-frame" src="/api/offer/${encodeURIComponent(token)}/pdf" title="Offer Letter"></iframe>
          </section>
          <section class="letter-page acceptance-page">
            <div class="section-title">Acceptance & Signature</div>
            <div class="acceptance-copy">
              <p>I confirm that I have read the complete offer letter above and understand the employment terms stated in it.</p>
              <p>By signing below, I accept this offer under the name written here.</p>
            </div>
            <div class="grid">
              <div class="field full"><label>Full Legal Name</label><input id="signedName" value="${escapeAttr(meta.employeeName)}"></div>
            </div>
            <label class="declaration-check"><input id="consent" type="checkbox"><span>I have read and accept the entire offer letter.</span></label>
            <div class="signature-box"><canvas id="signatureCanvas"></canvas><div class="sig-tools"><span>Sign with finger, mouse or trackpad.</span><button id="clearSig" class="secondary" type="button">Clear</button></div></div>
            <div id="turnstileWidget" class="turnstile"></div>
            <div id="result"></div>
            <div class="actions"><button id="decline" class="secondary">Decline Offer</button><button id="sign" class="primary">Accept & Sign</button></div>
          </section>`}
      </main>`;
    if (terminal) return;

    const pad = createSignaturePad(root.querySelector<HTMLCanvasElement>('#signatureCanvas')!);
    let turnstile: TurnstileControl = { getToken: () => '', reset: () => undefined };
    mountTurnstile(root.querySelector<HTMLElement>('#turnstileWidget')!, 'offer_response')
      .then((control) => { turnstile = control; })
      .catch((error) => show(root, error instanceof Error ? error.message : String(error), true));
    root.querySelector('#clearSig')?.addEventListener('click', pad.clear);
    root.querySelector('#sign')?.addEventListener('click', async () => {
      const name = (root.querySelector<HTMLInputElement>('#signedName')!).value.trim();
      const consent = root.querySelector<HTMLInputElement>('#consent')!.checked;
      if (!name || !consent || !pad.hasInk()) return show(root, 'Please complete name, consent and signature.', true);
      await mutate(root, `/api/offer/${encodeURIComponent(token)}/sign`, {
        signedName: name, consent, signatureDataUrl: pad.toDataUrl(), idempotencyKey: createIdempotencyKey('sign'), turnstileToken: turnstile.getToken()
      }, turnstile.reset);
    });
    root.querySelector('#decline')?.addEventListener('click', async () => {
      const reason = prompt('Optional reason for declining:');
      if (reason === null || !confirm('Confirm decline?')) return;
      await mutate(root, `/api/offer/${encodeURIComponent(token)}/decline`, {
        reason, idempotencyKey: createIdempotencyKey('decline'), turnstileToken: turnstile.getToken()
      }, turnstile.reset);
    });
  } catch (error) {
    root.innerHTML = `<main class="offer-page"><section class="letter-page"><h1>Offer unavailable</h1><p>${escapeHtml(error instanceof Error ? error.message : String(error))}</p></section></main>`;
  }
}

async function mutate(root: HTMLElement, url: string, payload: object, resetSecurity: () => void): Promise<void> {
  try {
    await apiJson(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    show(root, 'Completed successfully.', false);
    setTimeout(() => location.reload(), 900);
  } catch (error) {
    resetSecurity();
    show(root, error instanceof Error ? error.message : String(error), true);
  }
}
function show(root: HTMLElement, text: string, error: boolean): void { const el = root.querySelector<HTMLElement>('#result')!; el.className = `notice ${error ? 'error' : 'success'}`; el.textContent = text; }
function escapeHtml(value: string): string { return value.replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[c]!)); }
function escapeAttr(value: string): string { return escapeHtml(value); }
